// MARK: - Music Library (ported from MusicLibrary.swift + AudioDirector.swift)

import type { MusicMood, ContextualTag, AudioProfile } from './types';
import type { Mode } from '@/types/roast';

// ---------------------------------------------------------------------------
// Internal metadata shape (mirrors MusicMetadata in MusicLibrary.swift)
// ---------------------------------------------------------------------------

export interface MusicMeta {
  mood: MusicMood;
  /** 0.0–1.0, clamped */
  energy: number;
  contextTags: ContextualTag[];
  /**
   * Which persona's pool this track belongs to. Selection is pool-scoped, so a
   * roast never picks a toast track and vice-versa — add a `ToastMusic_*` entry
   * tagged `persona: 'toast'` and it joins the toast rotation automatically.
   */
  persona: Mode;
}

// ---------------------------------------------------------------------------
// MUSIC_LIBRARY – the SINGLE SOURCE OF TRUTH for music tracks. Each key is a
// track name that maps 1:1 to a file at web/public/music/<key>.mp3 (see
// web/public/music/README.md). Adding music = drop the file + add ONE entry
// here; the MusicTrack type and MUSIC_TRACKS array below derive from these keys,
// so no other file needs editing. `persona` scopes selection so a roast never
// picks a toast track (and vice-versa) — new ToastMusic_* entries are auto-eligible.
// ---------------------------------------------------------------------------

export const MUSIC_LIBRARY = {
  'RoastMusic_Jazzy': {
    mood: 'jazz',
    energy: 0.6,
    contextTags: ['casual', 'vintage'],
    persona: 'roast',
  },
  'ToastMusic_Romance': {
    mood: 'romantic',
    energy: 0.5,
    contextTags: ['formal', 'indoor'],
    persona: 'toast',
  },
  'ToastMusic_Sassy': {
    mood: 'playful', // inferred from the "Sassy" label — swap if the track reads differently
    energy: 0.8,
    contextTags: ['casual', 'party'],
    persona: 'toast',
  },
  'ToastMusic_Suave': {
    mood: 'sophisticated', // inferred from the "Suave" label — swap if the track reads differently
    energy: 0.4,
    contextTags: ['formal', 'professional'],
    persona: 'toast',
  },
} satisfies Record<string, MusicMeta>;

// ---------------------------------------------------------------------------
// Track name type + list — DERIVED from MUSIC_LIBRARY's keys (single source of
// truth). Re-exported by ./types so `import { MUSIC_TRACKS } from './types'`
// keeps working. Adding an entry above is all that's needed.
// ---------------------------------------------------------------------------

/** A cataloged track name. Resolves to /music/<MusicTrack>.mp3 via manifest.musicUrl. */
export type MusicTrack = keyof typeof MUSIC_LIBRARY;

/** All cataloged track names, in declaration order. */
export const MUSIC_TRACKS = Object.keys(MUSIC_LIBRARY) as MusicTrack[];

// ---------------------------------------------------------------------------
// contextualScore – mirrors calculateContextualScore in MusicLibrary.swift
// (tagScore * 0.7) + (energyScore * 0.3)
// ---------------------------------------------------------------------------

export function contextualScore(meta: MusicMeta, profile: AudioProfile): number {
  const metaTags = new Set(meta.contextTags);
  const profileTags = new Set(profile.contextualTags);

  const overlapCount = [...metaTags].filter((t) => profileTags.has(t)).length;
  const tagScore = overlapCount / Math.max(metaTags.size, profileTags.size || 1);

  const energyScore = 1 - Math.abs(meta.energy - profile.energyLevel);

  return tagScore * 0.7 + energyScore * 0.3;
}

// ---------------------------------------------------------------------------
// selectFromPool – mood-exact-match within a candidate pool, ranked by
// contextualScore; returns `fallback` when nothing in the pool matches the mood.
// (Generalizes MusicLibrary.selectMusic(for:) to any persona-scoped pool.)
// ---------------------------------------------------------------------------

export function selectFromPool(
  pool: [MusicTrack, MusicMeta][],
  profile: AudioProfile,
  fallback: MusicTrack,
): MusicTrack {
  const exactMatches = pool.filter(([, meta]) => meta.mood === profile.musicMood);

  if (exactMatches.length > 0) {
    // Among exact matches, pick the one with the highest contextual score
    const best = exactMatches.reduce((a, b) =>
      contextualScore(a[1], profile) >= contextualScore(b[1], profile) ? a : b,
    );
    return best[0];
  }

  return fallback;
}

/** Per-persona fallback track when no mood in the pool matches. */
const POOL_FALLBACK: Record<Mode, MusicTrack> = {
  roast: 'RoastMusic_Jazzy', // the only roast track
  toast: 'ToastMusic_Romance', // the toast romance default
};

/** Catalog entries belonging to one persona's pool. */
function poolFor(mode: Mode): [MusicTrack, MusicMeta][] {
  return (Object.entries(MUSIC_LIBRARY) as [MusicTrack, MusicMeta][]).filter(
    ([, meta]) => meta.persona === mode,
  );
}

// ---------------------------------------------------------------------------
// selectMusic – roast-pool selection (mirrors MusicLibrary.selectMusic(for:)),
// kept as a named helper; scans only roast tracks so it never leaks toast music.
// ---------------------------------------------------------------------------

export function selectMusic(profile: AudioProfile): MusicTrack {
  return selectFromPool(poolFor('roast'), profile, POOL_FALLBACK.roast);
}

// ---------------------------------------------------------------------------
// selectMusicForPersona – mirrors selectMusicForPersona in AudioDirector.swift.
// Each mode picks only from its own pool (mood + contextual score), so toast is
// now mood/tag-driven exactly like roast — and dropping a new `ToastMusic_*`
// entry into MUSIC_LIBRARY puts it into rotation with no other code change.
// ---------------------------------------------------------------------------

export function selectMusicForPersona(profile: AudioProfile, mode: Mode): MusicTrack {
  return selectFromPool(poolFor(mode), profile, POOL_FALLBACK[mode]);
}
